import { isBrandFile, isBrandPointer } from '@hub/common';
import { logger } from 'features/logger/logger';
import { isNavigatingAway } from 'shared/utils/pageLeaveSignal';
import { fetchBrandFile } from './fetchBrandFile';
import { fetchProtocolBrand } from './fetchProtocolBrand';
import { protocolBrandUrl } from './protocolBrandUrl';
import type { BrandFile, BrandPointer } from '@hub/common';

const CACHE_KEY = 'brand-cache-v2';
const NO_BRAND_KEY = 'brand-none';
const LAST_KEY = 'brand-last';
const MAX_ENTRIES = 20;

/** A protocol's brand: a pointer to a URL serving its file, and the file. */
export type CachedBrand = { pointer: BrandPointer; file: BrandFile };
type CacheEntry = CachedBrand & { protocolId: string };

const local = () => window.localStorage;
const session = () => window.sessionStorage;

const readJson = (storage: () => Storage, key: string): unknown => {
  try {
    const raw = storage().getItem(key);
    return raw === null ? undefined : (JSON.parse(raw) as unknown);
  } catch {
    return undefined;
  }
};

const writeJson = (storage: () => Storage, key: string, value: unknown) => {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    // Storage is unavailable; the brand is fetched again next time.
  }
};

const isCacheEntry = (item: unknown): item is CacheEntry => {
  if (typeof item !== 'object' || item === null) return false;
  const { protocolId, pointer, file } = item as Record<string, unknown>;
  return (
    typeof protocolId === 'string' &&
    isBrandPointer(pointer) &&
    isBrandFile(file)
  );
};

const readEntries = (): CacheEntry[] => {
  const value = readJson(local, CACHE_KEY);
  return Array.isArray(value) ? (value as unknown[]).filter(isCacheEntry) : [];
};

const readNoBrandIds = (): string[] => {
  const value = readJson(session, NO_BRAND_KEY);
  return Array.isArray(value)
    ? value.filter((id): id is string => typeof id === 'string')
    : [];
};

// null: the protocol is known to have no brand (this session only).
export const readCachedBrand = (
  protocolId: string
): CachedBrand | null | undefined => {
  const entry = readEntries().find(item => item.protocolId === protocolId);
  if (entry) return { pointer: entry.pointer, file: entry.file };
  return readNoBrandIds().includes(protocolId) ? null : undefined;
};

// Keeps the 20 most recently written protocols; reads do not reorder them.
export const writeCachedBrand = (
  protocolId: string,
  brand: CachedBrand | null
) => {
  const entries = readEntries();
  const others = entries.filter(item => item.protocolId !== protocolId);
  if (brand === null) {
    if (others.length < entries.length) writeJson(local, CACHE_KEY, others);
    writeJson(session, NO_BRAND_KEY, [
      ...readNoBrandIds().filter(id => id !== protocolId),
      protocolId,
    ]);
    return;
  }
  others.push({ protocolId, pointer: brand.pointer, file: brand.file });
  writeJson(local, CACHE_KEY, others.slice(-MAX_ENTRIES));
};

/** The brand last shown on a participant page and the protocol of the start
 *  link or participation it came from. */
type LastBrand = { protocolId: string; pointer: BrandPointer };

export const readLastBrand = (): LastBrand | undefined => {
  const value = readJson(local, LAST_KEY);
  if (!isBrandPointer(value)) return undefined;
  const { protocolId } = value as { protocolId?: unknown };
  return typeof protocolId === 'string'
    ? { protocolId, pointer: { id: value.id, url: value.url } }
    : undefined;
};

// The cached file of the last brand, whichever protocol cached it, without a
// request.
export const readLastBrandFile = (): BrandFile | undefined => {
  const last = readLastBrand();
  return last
    ? readEntries().find(item => item.pointer.id === last.pointer.id)?.file
    : undefined;
};

// Drops every cached brand but the entry of the last brand, and the "no
// brand" answers.
export const forgetBrandsButLast = () => {
  const last = readLastBrand();
  const kept = last
    ? readEntries().find(item => item.pointer.id === last.pointer.id)
    : undefined;
  try {
    if (kept) local().setItem(CACHE_KEY, JSON.stringify([kept]));
    else local().removeItem(CACHE_KEY);
    session().removeItem(NO_BRAND_KEY);
  } catch {
    // Storage is unavailable; nothing to forget.
  }
};

// Remembers the brand being shown and its protocol; null (a protocol without
// a brand) clears it.
export const rememberLastBrand = (
  protocolId: string,
  pointer: BrandPointer | null
) => {
  if (pointer === null) {
    try {
      local().removeItem(LAST_KEY);
    } catch {
      // Storage is unavailable; nothing to forget.
    }
  } else {
    writeJson(local, LAST_KEY, {
      protocolId,
      id: pointer.id,
      url: pointer.url,
    });
  }
};

// The file `pointer` names, from the cache when it holds that brand for the
// protocol, otherwise fetched and cached; null caches "no brand".
export const getPointedBrand = async (
  protocolId: string,
  pointer: BrandPointer | null,
  signal?: AbortSignal
): Promise<CachedBrand | null> => {
  if (pointer === null) {
    writeCachedBrand(protocolId, null);
    return null;
  }
  const cached = readCachedBrand(protocolId);
  if (cached?.pointer.id === pointer.id) return cached;
  const brand = { pointer, file: await fetchBrandFile(pointer.url, signal) };
  writeCachedBrand(protocolId, brand);
  return brand;
};

// The protocol's brand copy, cached; a missing copy is "no brand" for the
// session. A failed request gives no brand and is not cached; it logs a
// warning unless the page is leaving.
const loadProtocolBrand = async (
  protocolId: string
): Promise<CachedBrand | null> => {
  let file: BrandFile | null;
  try {
    file = await fetchProtocolBrand(protocolId);
  } catch (error) {
    if (!isNavigatingAway())
      logger.warn('Unable to load the protocol brand', {
        details: { protocolId, error },
      });
    return null;
  }
  const brand = file && {
    pointer: { id: file.brandId, url: protocolBrandUrl(protocolId) },
    file,
  };
  writeCachedBrand(protocolId, brand);
  return brand;
};

const pendingBrands = new Map<string, Promise<CachedBrand | null>>();

// The cached answer, brand or none, without a request; otherwise the
// protocol's brand copy, one request per protocol at a time. Never rejects.
export const getProtocolBrand = (
  protocolId: string
): Promise<CachedBrand | null> => {
  const cached = readCachedBrand(protocolId);
  if (cached !== undefined) return Promise.resolve(cached);
  let pending = pendingBrands.get(protocolId);
  if (pending === undefined) {
    pending = loadProtocolBrand(protocolId).finally(() => {
      pendingBrands.delete(protocolId);
    });
    pendingBrands.set(protocolId, pending);
  }
  return pending;
};
